import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { 
  MapPin, Clock, User, Phone, Car, Navigation, CheckCircle, 
  Loader2, AlertCircle, Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: "confirmed", label: "Booking Confirmed", icon: CheckCircle },
  { key: "dispatched", label: "Driver Assigned", icon: User },
  { key: "en_route", label: "Driver En Route", icon: Navigation },
  { key: "arrived", label: "Driver Arrived", icon: MapPin },
  { key: "trip_started", label: "Trip In Progress", icon: Car },
  { key: "completed", label: "Trip Completed", icon: CheckCircle },
];

const LiveTrackingPage = () => {
  const { bookingRef, trackingToken } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    fetchTracking();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchTracking, 10000);
    return () => clearInterval(interval);
  }, [bookingRef, trackingToken]);

  useEffect(() => {
    if (tracking?.location && window.google && mapRef.current) {
      initializeMap();
    }
  }, [tracking?.location, mapLoaded]);

  const fetchTracking = async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-track/${bookingRef}/${trackingToken}`);
      if (!response.ok) {
        throw new Error("Invalid tracking link");
      }
      const data = await response.json();
      setTracking(data);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load tracking information");
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!tracking?.location || !window.google || mapInstanceRef.current) return;

    const { latitude, longitude } = tracking.location;
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: latitude, lng: longitude },
      zoom: 15,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
        { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a4a" }] },
        { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e0e1a" }] },
      ],
      disableDefaultUI: true,
      zoomControl: true,
    });

    // Driver marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat: latitude, lng: longitude },
      map: mapInstanceRef.current,
      icon: {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 6,
        fillColor: "#C8A95B",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
        rotation: tracking.location.heading || 0,
      },
      title: "Your Driver",
    });
  };

  // Update marker position when location changes
  useEffect(() => {
    if (markerRef.current && tracking?.location) {
      const { latitude, longitude, heading } = tracking.location;
      markerRef.current.setPosition({ lat: latitude, lng: longitude });
      if (heading) {
        markerRef.current.setIcon({
          ...markerRef.current.getIcon(),
          rotation: heading,
        });
      }
      mapInstanceRef.current?.panTo({ lat: latitude, lng: longitude });
    }
  }, [tracking?.location]);

  // Load Google Maps script
  useEffect(() => {
    if (window.google) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY || ""}`;
    script.async = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const getCurrentStep = () => {
    if (!tracking) return 0;
    
    const { status, driver_status } = tracking;
    
    if (status === "completed") return 5;
    if (driver_status === "trip_started" || status === "trip_started") return 4;
    if (driver_status === "arrived") return 3;
    if (driver_status === "en_route" || status === "in_progress") return 2;
    if (status === "dispatched") return 1;
    if (status === "confirmed") return 0;
    
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#C8A95B] mx-auto mb-4" />
          <p className="text-white/60">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#111114] border-red-500/20">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Tracking Unavailable</h1>
            <p className="text-white/60">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStep = getCurrentStep();

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white" data-testid="live-tracking-page">
      {/* Header */}
      <header className="bg-[#111114] border-b border-white/[0.06] p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-8 w-auto mb-2" />
              <div className="flex items-center gap-2">
                <span className="font-mono text-[#C8A95B]">{tracking.booking_ref}</span>
                <Badge className={`text-[10px] ${
                  tracking.status === "completed" ? "bg-green-500/15 text-green-400" :
                  tracking.status === "cancelled" ? "bg-red-500/15 text-red-400" :
                  "bg-blue-500/15 text-blue-400"
                }`}>
                  {tracking.status.replace("_", " ").toUpperCase()}
                </Badge>
              </div>
            </div>
            {tracking.driver && (
              <a href={`tel:${tracking.driver.phone}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-colors">
                <Phone className="h-4 w-4" />
                <span className="text-sm font-medium">Call Driver</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Status Message */}
        <div className="p-4 rounded-xl bg-[#C8A95B]/10 border border-[#C8A95B]/20">
          <p className="text-[#C8A95B] font-medium">{tracking.status_message}</p>
          {tracking.driver_status_message && (
            <p className="text-[#C8A95B]/70 text-sm mt-1">{tracking.driver_status_message}</p>
          )}
        </div>

        {/* Progress Steps */}
        <div className="space-y-2">
          {STATUS_STEPS.map((step, index) => {
            const isCompleted = index <= currentStep;
            const isCurrent = index === currentStep;
            const StepIcon = step.icon;

            return (
              <div key={step.key} className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                isCurrent ? "bg-[#C8A95B]/10 border border-[#C8A95B]/20" :
                isCompleted ? "bg-white/[0.02]" : "opacity-40"
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? "bg-[#C8A95B]/20" : "bg-white/[0.04]"
                }`}>
                  <StepIcon className={`h-4 w-4 ${isCompleted ? "text-[#C8A95B]" : "text-white/30"}`} />
                </div>
                <span className={`text-sm ${isCompleted ? "text-white" : "text-white/40"}`}>
                  {step.label}
                </span>
                {isCurrent && (
                  <div className="ml-auto flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-[#C8A95B] animate-pulse" />
                    <span className="text-[10px] text-[#C8A95B]">Current</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Map (only show if location available) */}
        {tracking.location && (
          <div className="rounded-xl overflow-hidden border border-white/[0.06]">
            <div ref={mapRef} className="h-64 w-full bg-[#111114]" data-testid="tracking-map" />
            <div className="p-3 bg-[#111114] border-t border-white/[0.06]">
              <p className="text-xs text-white/40">
                Location updated: {new Date(tracking.location.updated_at).toLocaleTimeString()}
              </p>
            </div>
          </div>
        )}

        {/* Driver Info */}
        {tracking.driver && (
          <Card className="bg-[#111114] border-white/[0.06]">
            <CardContent className="p-4">
              <h3 className="text-xs text-white/40 uppercase tracking-wider mb-3">Your Driver</h3>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#C8A95B]/15 flex items-center justify-center">
                  <User className="h-7 w-7 text-[#C8A95B]" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-lg">{tracking.driver.name}</p>
                  {tracking.driver.rating && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-sm text-white/60">{tracking.driver.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <a href={`tel:${tracking.driver.phone}`} className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center hover:bg-green-500/25 transition-colors">
                  <Phone className="h-5 w-5 text-green-400" />
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trip Details */}
        <Card className="bg-[#111114] border-white/[0.06]">
          <CardContent className="p-4 space-y-4">
            <h3 className="text-xs text-white/40 uppercase tracking-wider">Trip Details</h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-4 w-4 text-green-400" />
                </div>
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">Pickup</p>
                  <p className="text-sm">{tracking.pickup_location}</p>
                </div>
              </div>
              
              {tracking.dropoff_location && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="h-4 w-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Dropoff</p>
                    <p className="text-sm">{tracking.dropoff_location}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#C8A95B]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="h-4 w-4 text-[#C8A95B]" />
                </div>
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">Scheduled</p>
                  <p className="text-sm">{new Date(tracking.pickup_datetime).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Car className="h-4 w-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider">Vehicle</p>
                  <p className="text-sm capitalize">{tracking.vehicle_type?.replace(/_/g, " ")}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-white/30 text-xs">
            Limo Near Me Australia | +61 481 437 772
          </p>
        </div>
      </main>
    </div>
  );
};

export default LiveTrackingPage;
